import "./AutoRefresh.css";
import React, { useEffect, useRef, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, Tooltip } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { REFRESH_INTERVALS, parseRefresh } from "./refreshParams";

interface AutoRefreshControlProps {
  /** Called on every tick. Kept in a ref so changing filters doesn't restart the timer. */
  onRefresh: () => void;
  /** Suppresses the countdown while a request is already in flight. */
  busy?: boolean;
  /** "sidebar" stacks under the filters (legacy pages); "toolbar" sits inline in a DataTable toolbar. */
  variant?: "sidebar" | "toolbar";
}

const label = (seconds: number) => (seconds === 0 ? "Off" : `${seconds}s`);

const AutoRefreshControl: React.FC<AutoRefreshControlProps> = ({ onRefresh, busy, variant = "sidebar" }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const interval = parseRefresh(searchParams);
  const [countdown, setCountdown] = useState(interval);

  // The callback closes over the current filters/sort/page, but the timer must survive
  // those changing — so read it through a ref instead of listing it as an effect dep.
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (interval === 0) {
      setCountdown(0);
      return;
    }
    setCountdown(interval);
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onRefreshRef.current();
          return interval;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [interval]);

  const handleChange = (value: number) => {
    const next = new URLSearchParams(searchParams);
    value === 0 ? next.delete("refresh") : next.set("refresh", String(value));
    setSearchParams(next);
  };

  const inToolbar = variant === "toolbar";

  return (
    <div className={`auto-refresh${inToolbar ? " auto-refresh-toolbar" : ""}`}>
      <FormControl size="small" fullWidth={!inToolbar} className={inToolbar ? "lp-bulk-select" : undefined}>
        <InputLabel id="auto-refresh-label">Auto-refresh</InputLabel>
        <Select
          labelId="auto-refresh-label"
          id="auto-refresh"
          label="Auto-refresh"
          value={interval}
          onChange={(e) => handleChange(Number(e.target.value))}
        >
          {REFRESH_INTERVALS.map((seconds) => (
            <MenuItem key={seconds} value={seconds}>{label(seconds)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {interval > 0 && (
        <Tooltip title="Refetches with your current filters, sort and page. The page itself never reloads.">
          <span className="auto-refresh-countdown">
            <span className={`auto-refresh-dot${busy ? " is-busy" : ""}`} />
            {busy ? "Refreshing..." : `Next refresh in ${countdown}s`}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default AutoRefreshControl;
